import { useEffect, useRef } from 'react';
import { useEditorStore } from './store/editor.store';
import { EditorToolbar } from './editor/EditorToolbar';
import { LeftPanel } from './editor/LeftPanel';
import { Canvas } from './editor/canvas/Canvas';
import { PropertiesPanel } from './editor/panels/PropertiesPanel';
import { DndEngine } from './editor/DndEngine';
import type { Design, BuilderMode } from './types/editor.types';
import { exportHtml, exportJson } from './services/export';
import { designFromHtml } from './services/htmlDesign';
import { registerTemplateEditorBridge } from '../chat-assistant/templateBridge';
import { downloadBlob } from './utils';

interface EmbeddedEditorProps {
  /** Stable key from parent — changing this re-mounts the component and re-inits the editor */
  editorKey?: number | string;
  templateName: string;
  mode?: BuilderMode;
  initialDesign?: Design;
  onSave?: (design: Design) => void | Promise<void>;
  onBack?: () => void;
  /** Set once the open template exists server-side, so an assistant-driven
      save updates it rather than creating a duplicate. */
  templateId?: number | string | null;
}

export function EmbeddedEditor({
  templateName,
  mode = 'email',
  initialDesign,
  onSave,
  onBack,
  templateId,
}: EmbeddedEditorProps) {
  const { initEditor, design, loadDesign } = useEditorStore();

  /* Keep the newest design in a ref so the bridge can read it without being
     re-registered on every keystroke. */
  const designRef = useRef(design);
  designRef.current = design;

  /* Announce this editor to the chat socket while it is mounted.
     `template_generated` then replaces the document in place, and
     `template_save_request` saves what is on screen — including manual edits —
     rather than the model's last output. */
  useEffect(
    () =>
      registerTemplateEditorBridge({
        loadHtml: (html) => loadDesign(designFromHtml(html)),
        getDesign: () => designRef.current ?? null,
        getTemplateId: () => templateId ?? null,
        getName: () => templateName ?? null,
      }),
    [loadDesign, templateId, templateName],
  );

  // Init on mount only — re-initialization is controlled by the parent
  // via the React `key` prop on this component, not by internal prop changes.
  useEffect(() => {
    initEditor({ mode }, initialDesign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (onSave) await onSave(design);
  };

  const handleExportHtml = () => {
    const html = exportHtml(design);
    downloadBlob(html, `${templateName}.html`, 'text/html');
  };

  const handleExportJson = () => {
    const json = exportJson(design);
    downloadBlob(json, `${templateName}.json`, 'application/json');
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[#f0f2f8] dark:bg-dark-950">
      <DndEngine>
        <EditorToolbar
          templateName={templateName}
          onBack={onBack}
          onSave={handleSave}
          onExportHtml={handleExportHtml}
          onExportJson={handleExportJson}
        />
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <LeftPanel />
          <Canvas />
          <aside className="w-72 shrink-0 flex flex-col overflow-hidden
            border-l border-line/70 dark:border-dark-700
            bg-surface dark:bg-dark-800 shadow-[-4px_0_16px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_28px_rgba(0,0,0,0.25)]">
            <PropertiesPanel />
          </aside>
        </div>
      </DndEngine>
    </div>
  );
}
